import { Ohje } from "./Ohje";
import { tDok, t, virheTeksti } from "./kieli";
import { useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import {
  api, ApiError,
  type Kayttaja, type Henkilo,
  type OsaamisRata, type OsaamisTaso, type OsaamisNimitys, type OsaamisSuoritus,
  type SertiEhdot, type Arviointi, type Sertifikaatti,
  type SertifikaattiTiedot,
} from "./api";
import { haeBrandi, type Brandi } from "./brandi";
import { TulosteLeima } from "./Tuloste";

/* ═══════════════════════════════════════════════════════════
   Osaamisradat.

   Tasoa ei aseteta täältä eikä mistään muualtakaan: se johdetaan
   kirjatuista vaihesuorituksista. Näkymässä on siis kaksi asiaa —
   kirjaus (mitä tein) ja kortti (mihin se johti) — eikä yhtään
   kenttää johon tason voisi kirjoittaa.

   Hyvitetty ja mitattu näytetään aina erikseen. Se on hyvityksen
   hinta ja samalla sen turva: kun kysytään montako Senioria on
   hyvityksellä ja montako mitattuna, kysymykseen on vastaus.
   ═══════════════════════════════════════════════════════════ */

export default function Osaaminen({ kayttaja }: { kayttaja: Kayttaja }) {
  const [radat, setRadat] = useState<OsaamisRata[]>([]);
  const [henkilot, setHenkilot] = useState<Henkilo[]>([]);
  const [kortit, setKortit] = useState<(OsaamisTaso & { nimi: string })[]>([]);
  const [valittu, setValittu] = useState<number | null>(null);
  const [ladataan, setLadataan] = useState(true);
  const [virhe, setVirhe] = useState<string | null>(null);
  /* Kielletty on eri tila kuin tyhjä. Ilman tätä lippua "ei kortteja"
   * ja "et saa nähdä kortteja" näyttäisivät identtisiltä. */
  const [kortitKielletty, setKortitKielletty] = useState(false);

  const saaNimittaa = kayttaja.oikeudet.includes("osaaminen_nimitys");
  const saaArvioida = kayttaja.oikeudet.includes("osaaminen_arviointi");
  const saaAsettaaRajat = kayttaja.oikeudet.includes("osaaminen_rajat");

  /*
   * allSettled eikä all: YKSI 403 EI SAA KAATAA KOKO NÄKYMÄÄ.
   *
   * Mitattu 1.9.2026 lukijatunnuksella: `osaamiskortit()` vastaa 403,
   * `Promise.all` hylkää, eivätkä radat ja henkilöt koskaan asetu —
   * ruudulla on pelkkä "Sinulla ei ole oikeutta tähän toimintoon".
   * Näkymä ei siis kertonut mitä se olisi voinut kertoa, eikä se
   * kertonut mikä osa oli kielletty.
   *
   * Nyt jokainen osa kertoo itse tilansa, ja kielletty osa merkitään
   * ERIKSEEN tyhjästä: "ei oikeutta" ja "ei kortteja" ovat eri asioita
   * eivätkä ne saa näyttää samalta (§Tuntematon ei ole nolla).
   */
  const lataa = useCallback(async () => {
    const [vr, vh, vk] = await Promise.allSettled([
      // api.henkilot() eikä api.resurssit(): Academy ei lue
      // resursoinnista. Ks. PAATOKSET §Academy ei lue resursoinnista.
      api.osaamisradat(), api.henkilot(), api.osaamiskortit(),
    ]);
    if (vr.status === "fulfilled") setRadat(vr.value);
    if (vh.status === "fulfilled") setHenkilot(vh.value.filter((x) => !x.nimeamaton));
    if (vk.status === "fulfilled") setKortit(vk.value);
    setKortitKielletty(vk.status === "rejected"
      && vk.reason instanceof ApiError && vk.reason.virhe === "ei_oikeutta");

    // Virhe vain siitä mikä oikeasti epäonnistui, ja vain jos se ei
    // ole oikeuskysymys — oikeus on tila, ei vika.
    const viat = [vr, vh, vk].filter((v) => v.status === "rejected"
      && !(v.reason instanceof ApiError && v.reason.virhe === "ei_oikeutta"));
    setVirhe(viat.length
      ? (viat[0] as PromiseRejectedResult).reason instanceof ApiError
        ? virheTeksti(((viat[0] as PromiseRejectedResult).reason as ApiError).virhe, ((viat[0] as PromiseRejectedResult).reason as ApiError).message)
        : t("osa.tiedot_virhe")
      : null);
    setLadataan(false);
  }, []);

  useEffect(() => { void lataa(); }, [lataa]);

  if (ladataan) return <p className="dim">{t("ltar.ladataan")}</p>;

  return (
    <>
      {/* Otsikko puuttui kokonaan — tämä oli ainoa 27 näkymästä ilman
          `h1`:tä. Löytyi 2.9.2026 pääkäyttäjän kävelyssä, ja vasta
          siksi että `kavely.mjs`:n yhteenvetorivi alkoi samana aamuna
          nimetä vialliset näkymät. Lukijan navissa Osaamisradat ei
          näy lainkaan, joten aiemmat lukijatunnuksella ajetut
          kävelyt eivät voineet nähdä tätä. */}
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.osaamisradat")}</h1></div>
      </header>

      {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/osaaminen*: kortit paakayttaja/suunnittelija/tyonjohtaja; oikeudet osaaminen_rajat, osaaminen_nimitys, osaaminen_arviointi (069, 138). */}
      <Ohje
        kuka={<>{t("osa.ohje.kuka")}</>}
        luvut={<>{t("osa.ohje.luvut")}</>} />

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {kortitKielletty && (
        <p className="huom" role="status">
          {t("osa.kortit_kielletty")}
        </p>
      )}

      {saaAsettaaRajat && <Hallinta radat={radat} onMuutos={lataa} />}

      <Kirjaus radat={radat} henkilot={henkilot} kayttaja={kayttaja}
               saaArvioida={saaArvioida} onKirjattu={lataa} />

      <Matriisi kortit={kortit} radat={radat}
                valitse={(id) => setValittu(id === valittu ? null : id)}
                valittu={valittu} />

      {valittu !== null && (
        /* Päätös 91 (Toni 6.10.2026): key = henkilö. Valinnan vaihto purkaa kortin, joten edellisen henkilön myöhästynyt
           vastaus ei täytä tätä korttia eikä «Tulosta kortti» tulosta toisen henkilön tietoja tämän nimellä. */
        <Kortti key={valittu} henkiloId={valittu} radat={radat} saaNimittaa={saaNimittaa}
                saaArvioida={saaArvioida} omaId={kayttaja.resurssi_id}
                nimi={henkilot.find((h) => h.id === valittu)?.nimi ?? "—"}
                onMuutos={lataa} />
      )}
    </>
  );
}

/* ── Kenttäkirjaus ─────────────────────────────────────────
   Puhelimella tehtävä kirjaus: rata → vaihe → yksikkö → tallenna.
   Oma suoritus kirjautuu ilman erillistä valintaa; toisen puolesta
   kirjaaminen näkyy vain arviointioikeuden haltijalle. */

function Kirjaus({
  radat, henkilot, kayttaja, saaArvioida, onKirjattu,
}: {
  radat: OsaamisRata[]; henkilot: Henkilo[]; kayttaja: Kayttaja;
  saaArvioida: boolean; onKirjattu: () => Promise<void>;
}) {
  const [rataId, setRataId] = useState<number | "">("");
  const [vaiheId, setVaiheId] = useState<number | "">("");
  const [yksikko, setYksikko] = useState("");
  const [kenelle, setKenelle] = useState<number | "">("");
  const [vastuulla, setVastuulla] = useState(false);
  const [hyvitys, setHyvitys] = useState(false);
  const [peruste, setPeruste] = useState("");
  const [tallennetaan, setTallennetaan] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);

  const [omatRadat, setOmatRadat] = useState<number[] | null>(null);
  const omaHenkilo = kayttaja.resurssi_id;
  const kohde = kenelle === "" ? omaHenkilo : kenelle;

  /*
   * Radat rajataan niihin joille kohdehenkilö on nimitetty — ne ovat
   * hänen ammattinsa työmaalla, ja useampi on tavallista.
   *
   * Rajaus ei ole kosmetiikkaa: f_osaamistaso lähtee liikkeelle
   * nimityksistä, joten muulle radalle kirjattu suoritus tallentuisi
   * eikä laskisi mitään. Palvelin torjuu sen, mutta valikossa ei ole
   * mitään syytä näyttää vaihtoehtoa jota ei voi valita.
   */
  useEffect(() => {
    if (!kohde) { setOmatRadat([]); return; }
    let voimassa = true;
    api.osaamiskortti(kohde)
      .then((k) => { if (voimassa) setOmatRadat(
        k.nimitykset.filter((n) => !n.peruttu).map((n) => n.rata_id)); })
      .catch(() => { if (voimassa) setOmatRadat([]); });
    return () => { voimassa = false; };
  }, [kohde]);

  const sallitut = radat.filter(
    (r) => r.aktiivinen && (omatRadat === null || omatRadat.includes(r.id)));
  const rata = sallitut.find((r) => r.id === rataId);

  async function tallenna() {
    if (vaiheId === "") { setIlmoitus(t("osa.valitse_vaihe")); return; }
    if (hyvitys && !peruste.trim()) {
      setIlmoitus(t("osa.hyvitys_peruste_puuttuu"));
      return;
    }
    setTallennetaan(true); setIlmoitus(null);
    try {
      await api.kirjaaVaihe({
        vaihe_id: vaiheId,
        henkilo_id: kenelle === "" ? null : kenelle,
        yksikko: yksikko.trim() || null,
        vastuuhenkilona: vastuulla,
        hyvitys,
        peruste: peruste.trim() || null,
      });
      setIlmoitus(t("osa.kirjattu"));
      setRataId(""); setVaiheId("");
      setYksikko(""); setPeruste(""); setHyvitys(false); setVastuulla(false);
      await onKirjattu();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("osa.kirjaus_virhe"));
    } finally { setTallennetaan(false); }
  }

  if (radat.length === 0) {
    return (
      <section className="panel">
        <div className="panel__head"><h2>{t("osa.vaihekirjaus")}</h2></div>
        <p className="dim">
          {t("osa.ei_ratoja")}
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{t("osa.vaihekirjaus")}</h2>
        <button className="nappi nappi--paa" disabled={tallennetaan || vaiheId === ""}
                onClick={() => void tallenna()}>
          {tallennetaan ? t("osa.kirjataan") : t("osa.kirjaa_suoritus")}
        </button>
        {!tallennetaan && vaiheId === "" && <span className="este-syy" data-este-syy="">{t("osa.syy_vaihe")}</span>}
      </div>

      <p className="ikkuna__vihje" style={{ margin: "0 0 12px" }}>
        {t("osa.kirjaus_vihje")}
      </p>

      {omatRadat !== null && sallitut.length === 0 && (
        <p className="huom" role="status">
          {kohde
            ? t("osa.ei_nimityksia_kirjaus")
            : t("osa.ei_henkiloa_kirjaus")}
        </p>
      )}

      <div className="lomake__rivi">
        <label>
          {t("osa.rata")}
          <select value={rataId} onChange={(e) => {
            setRataId(e.target.value === "" ? "" : Number(e.target.value));
            setVaiheId("");
          }}>
            <option value="">{t("osa.valitse")}</option>
            {sallitut.map((r) => (
              <option key={r.id} value={r.id}>{r.nimi}</option>
            ))}
          </select>
        </label>

        <label>
          {t("osa.vaihe")}
          <select value={vaiheId} disabled={!rata}
                  onChange={(e) => setVaiheId(e.target.value === "" ? "" : Number(e.target.value))}>
            <option value="">{t("osa.valitse")}</option>
            {(rata?.vaiheet ?? []).filter((v) => v.aktiivinen).map((v) => (
              <option key={v.id} value={v.id}>{v.nimi}</option>
            ))}
          </select>
        </label>

        <label>
          {t("osa.yksikko")}
          <input type="text" value={yksikko} placeholder={t("osa.yksikko_esim")}
                 onChange={(e) => setYksikko(e.target.value)} />
        </label>
      </div>

      <div className="osaamiset" style={{ marginTop: 10 }}>
        <label className={"valinta" + (vastuulla ? " valinta--on" : "")}>
          <input type="checkbox" checked={vastuulla}
                 onChange={() => setVastuulla(!vastuulla)} />
          {t("osa.vastuuhenkilona")}
        </label>
      </div>

      {saaArvioida && (
        <>
          <h3 style={{ marginTop: 20 }}>{t("osa.toisen_puolesta")}</h3>
          <div className="lomake__rivi">
            <label>
              {t("tk.henkilo")}
              <select value={kenelle}
                      onChange={(e) => setKenelle(e.target.value === "" ? "" : Number(e.target.value))}>
                <option value="">{omaHenkilo ? t("osa.itselleni") : t("osa.valitse")}</option>
                {henkilot.map((h) => (
                  <option key={h.id} value={h.id}>{h.nimi}</option>
                ))}
              </select>
            </label>
            <label>
              {t("osa.peruste")} {hyvitys && <span style={{ color: "var(--puna)" }}>*</span>}
              <input type="text" value={peruste}
                     placeholder={hyvitys ? t("osa.peruste_hyvitys_vihje") : t("osa.vapaaehtoinen")}
                     onChange={(e) => setPeruste(e.target.value)} />
            </label>
          </div>
          <div className="osaamiset" style={{ marginTop: 10 }}>
            <label className={"valinta" + (hyvitys ? " valinta--on" : "")}>
              <input type="checkbox" checked={hyvitys}
                     onChange={() => setHyvitys(!hyvitys)} />
              {t("osa.hyvitys_aiemmasta")}
            </label>
          </div>
          {hyvitys && (
            <p className="ikkuna__vihje" style={{ marginTop: 8 }}>
              {t("osa.hyvitys_vihje")}
            </p>
          )}
        </>
      )}

      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
    </section>
  );
}

/* ── Osaamismatriisi ───────────────────────────────────────
   Se näkymä jota tilaajalle näytetään: kuka osaa mitä, ja millä
   perusteella. Rivi per henkilö, sarake per rata. */

function Matriisi({
  kortit, radat, valitse, valittu,
}: {
  kortit: (OsaamisTaso & { nimi: string })[]; radat: OsaamisRata[];
  valitse: (id: number) => void; valittu: number | null;
}) {
  const henkilot = [...new Map(kortit.map((k) => [k.henkilo_id, k.nimi])).entries()];

  if (henkilot.length === 0) {
    return (
      <section className="panel">
        <div className="panel__head"><h2>{t("osa.matriisi")}</h2></div>
        <p className="dim">
          {t("osa.matriisi_tyhja")}
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{t("osa.matriisi")}</h2>
        <span className="dim">{t("osa.henkiloa", { n: henkilot.length })}</span>
      </div>
      <div className="taulukko-kaari">
        <table className="tbl">
          <thead>
            <tr>
              <th className="th--vasen">{t("tk.henkilo")}</th>
              {radat.map((r) => <th key={r.id} className="th--vasen">{r.nimi}</th>)}
            </tr>
          </thead>
          <tbody>
            {henkilot.map(([id, nimi]) => (
              <tr key={id} className={valittu === id ? "rivi--valittu" : undefined}>
                <td>
                  <button className="linkki" onClick={() => valitse(id)}>{nimi}</button>
                </td>
                {radat.map((r) => {
                  const tk = kortit.find((k) => k.henkilo_id === id && k.rata_id === r.id);
                  if (!tk) return <td key={r.id} className="dim">—</td>;
                  return (
                    <td key={r.id}>
                      <b>{tk.taso ?? "—"}</b>
                      {tk.lepaava && <span className="dim"> · {t("osa.lepaava")}</span>}
                      <span className="dim" style={{ display: "block" }}>
                        {t("osa.mitattu_lkm", { n: tk.mitatut })}{tk.hyvitetyt > 0 && ` · ${t("osa.hyvitetty_lkm", { n: tk.hyvitetyt })}`}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ikkuna__vihje">
        {t("osa.matriisi_vihje")}
      </p>
    </section>
  );
}

/* ── Osaamiskortti ─────────────────────────────────────────
   Yhden henkilön koko tilanne. Tulostettava, koska harjoittelijalle
   tämä on harjoittelun todistus ja tilaajalle näyttö siitä että
   luvut ovat jäljitettäviä. */

function Kortti({
  henkiloId, nimi, radat, saaNimittaa, saaArvioida, omaId, onMuutos,
}: {
  henkiloId: number; nimi: string; radat: OsaamisRata[];
  saaNimittaa: boolean; saaArvioida: boolean; omaId: number | null;
  onMuutos: () => Promise<void>;
}) {
  const [tasot, setTasot] = useState<OsaamisTaso[]>([]);
  const [nimitykset, setNimitykset] = useState<OsaamisNimitys[]>([]);
  const [suoritukset, setSuoritukset] = useState<OsaamisSuoritus[]>([]);
  const [uusiRata, setUusiRata] = useState<number | "">("");
  const [peruste, setPeruste] = useState("");
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [ladataan, setLadataan] = useState(true);
  /** Kenen tiedot kortilla on (vastauksen henkilo_id) — kävely vertaa tätä valittuun. */
  const [ladattuHenkilo, setLadattuHenkilo] = useState<number | null>(null);

  const lataa = useCallback(async () => {
    setLadataan(true);
    try {
      const [k, s] = await Promise.all([
        api.osaamiskortti(henkiloId), api.osaamissuoritukset(henkiloId),
      ]);
      setTasot(k.tasot); setNimitykset(k.nimitykset); setSuoritukset(s); setLadattuHenkilo(k.henkilo_id);
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("osa.kortti_virhe"));
    } finally { setLadataan(false); }
  }, [henkiloId]);

  useEffect(() => { void lataa(); }, [lataa]);

  async function nimita() {
    if (uusiRata === "") return;
    setIlmoitus(null);
    try {
      await api.nimitaRadalle({
        henkilo_id: henkiloId, rata_id: uusiRata, peruste: peruste.trim() || null,
      });
      setUusiRata(""); setPeruste("");
      await lataa(); await onMuutos();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("osa.nimitys_virhe"));
    }
  }

  const avoimet = nimitykset.filter((n) => !n.peruttu);
  const vapaat = radat.filter((r) => !avoimet.some((n) => n.rata_id === r.id));

  return (
    <section className="panel tuloste">
      <div className="panel__head">
        <h2 data-kortti-henkilo={henkiloId} data-kortti-tiedot={ladattuHenkilo ?? ""}>{nimi}</h2>
        <button className="nappi nappi--kevyt tuloste-tyokalut"
                onClick={() => window.print()}>{t("osa.tulosta_kortti")}</button>
      </div>

      {ladataan ? <p className="dim">{t("ltar.ladataan")}</p> : (
        <>
          <h3>{t("osa.johdettu_taso")}</h3>
          {tasot.length === 0 ? (
            <p className="dim">{t("osa.ei_nimityksia_kortti")}</p>
          ) : (
            <div className="taulukko-kaari">
              <table className="tbl">
                <thead>
                  <tr>
                    <th className="th--vasen">{t("osa.rata")}</th>
                    <th className="th--vasen">{t("osa.taso")}</th>
                    <th>{t("osa.mitattu")}</th>
                    <th>{t("osa.hyvitetty")}</th>
                    <th>{t("osa.yksikoita")}</th>
                    <th>{t("osa.vastuulla")}</th>
                    <th>{t("osa.ketjuja")}</th>
                    <th>{t("osa.viimeisin")}</th>
                  </tr>
                </thead>
                <tbody>
                  {tasot.map((tk) => (
                    <tr key={tk.rata_id}>
                      <td>{tk.rata}</td>
                      <td>
                        <b>{tk.taso ?? "—"}</b>
                        {tk.lepaava && <span className="dim"> · {t("osa.lepaava")}</span>}
                      </td>
                      <td className="mono">{tk.mitatut}</td>
                      <td className="mono dim">{tk.hyvitetyt || "—"}</td>
                      <td className="mono">{tk.eri_yksikkoa}</td>
                      <td className="mono">{tk.vastuulla}</td>
                      <td className="mono">{tk.ketjuja}</td>
                      <td className="mono">{tk.viimeisin ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 style={{ marginTop: 22 }}>{t("osa.nimitykset")}</h3>
          {avoimet.length === 0 && <p className="dim">{t("osa.ei_avoimia_nimityksia")}</p>}
          <ul className="lista">
            {nimitykset.map((n) => (
              <li key={n.id}>
                <b>{n.rata}</b> · {n.alkaen}
                {n.myonsi && <span className="dim"> · {t("osa.myonsi", { nimi: n.myonsi })}</span>}
                {n.peruste && <span className="dim"> · {n.peruste}</span>}
                {n.peruttu && (
                  <span className="dim"> — {t("osa.peruttu_pieni")}{n.perui && ` (${n.perui})`}
                    {n.perumisen_syy && `: ${n.perumisen_syy}`}</span>
                )}
              </li>
            ))}
          </ul>

          {saaNimittaa && vapaat.length > 0 && (
            <div className="lomake__rivi tuloste-tyokalut" style={{ marginTop: 12 }}>
              <label>
                {t("osa.nimita_radalle")}
                <select value={uusiRata}
                        onChange={(e) => setUusiRata(e.target.value === "" ? "" : Number(e.target.value))}>
                  <option value="">{t("osa.valitse")}</option>
                  {vapaat.map((r) => <option key={r.id} value={r.id}>{r.nimi}</option>)}
                </select>
              </label>
              <label>
                {t("osa.peruste")}
                <input type="text" value={peruste}
                       onChange={(e) => setPeruste(e.target.value)} />
              </label>
              <button className="nappi nappi--kevyt" disabled={uusiRata === ""}
                      onClick={() => void nimita()}>{t("osa.nimita")}</button>
            </div>
          )}

          <h3 style={{ marginTop: 22 }}>{t("osa.suoritukset")}</h3>
          {suoritukset.length === 0 ? (
            <p className="dim">{t("osa.ei_suorituksia")}</p>
          ) : (
            <div className="taulukko-kaari">
              <table className="tbl">
                <thead>
                  <tr>
                    <th className="th--vasen">{t("osa.paiva")}</th>
                    <th className="th--vasen">{t("osa.rata")}</th>
                    <th className="th--vasen">{t("osa.vaihe")}</th>
                    <th className="th--vasen">{t("osa.yksikko")}</th>
                    <th className="th--vasen">{t("osa.alkupera")}</th>
                  </tr>
                </thead>
                <tbody>
                  {suoritukset.map((s) => (
                    <tr key={s.id}>
                      <td className="mono">{naytaPvm(s.paiva)}</td>
                      <td>{s.rata}</td>
                      <td>{s.vaihe}{s.vastuuhenkilona && <span className="dim"> · {t("osa.vastuulla_pieni")}</span>}</td>
                      <td className="mono">{s.yksikko ?? "—"}</td>
                      <td className={s.hyvitys ? "dim" : undefined}>
                        {s.hyvitys
                          ? `${t("osa.hyvitys_pieni")}${s.myonsi ? ` · ${s.myonsi}` : ""}${s.peruste ? ` · ${s.peruste}` : ""}`
                          : t("osa.mitattu_pieni")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Sertifikaatit henkiloId={henkiloId}
                         saaArvioida={saaArvioida} omaId={omaId} />

          {ilmoitus && <p className="huom huom--vika" role="alert">{ilmoitus}</p>}
        </>
      )}
    </section>
  );
}

/* ── Ratojen ja vaiheiden hallinta ─────────────────────────
   Näkyy vain rajojen asettajalle. Malli on ehdotus jonka voi luoda
   yhdellä painalluksella ja muokata heti — nimet, järjestys, rajat
   ja säännöt ovat dataa.

   Poisto on eri asia kuin piilotus. Käytössä olevaa vaihetta ei voi
   poistaa: siihen kirjatut suoritukset ovat jonkun osaamiskertymän
   perusta, ja kannassa oleva CASCADE veisi ne mukanaan hiljaa.
   Palvelin torjuu, ja tämä näkymä kertoo miksi. */

function Hallinta({
  radat, onMuutos,
}: { radat: OsaamisRata[]; onMuutos: () => Promise<void> }) {
  const [avoin, setAvoin] = useState<number | null>(null);
  const [tyossa, setTyossa] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);

  async function aja(teko: () => Promise<unknown>, viesti?: string) {
    setTyossa(true); setIlmoitus(null);
    try {
      await teko();
      if (viesti) setIlmoitus(viesti);
      await onMuutos();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("aca.toiminto_virhe"));
    } finally { setTyossa(false); }
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{t("osa.radat_ja_vaiheet")}</h2>
        {radat.length === 0 && (
          <button className="nappi nappi--paa" disabled={tyossa}
                  onClick={() => void aja(() => api.luoOsaamismalli(), t("osa.mallirakenne_luotu"))}>
            {t("osa.luo_mallirakenne")}
          </button>
        )}
        {radat.length > 0 && (
          <button className="nappi nappi--kevyt" disabled={tyossa}
                  onClick={() => void aja(() => api.luoOsaamismalli(), t("osa.mallin_osat_taydennetty"))}>
            {t("osa.taydenna_mallista")}
          </button>
        )}
      </div>

      {radat.length === 0 && (
        <p className="dim">
          {t("osa.mallirakenne_vihje")}
        </p>
      )}

      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}

      {radat.map((r) => (
        <div key={r.id} className="laajennus" style={{ marginBottom: 10 }}>
          <div className="lomake__rivi" style={{ alignItems: "flex-end" }}>
            <label style={{ flex: "1 1 220px" }}>
              {t("osa.rata")}
              <input type="text" defaultValue={r.nimi} disabled={tyossa}
                     onBlur={(e) => e.target.value !== r.nimi
                       && void aja(() => api.muokkaaRataa(r.id, { nimi: e.target.value }))} />
            </label>
            <label style={{ flex: "0 0 130px" }}>
              {t("osa.tuoreus_kk")}
              <input type="number" min={1} defaultValue={r.tuoreus_kk} disabled={tyossa}
                     onBlur={(e) => Number(e.target.value) !== r.tuoreus_kk
                       && void aja(() => api.muokkaaRataa(r.id, { tuoreus_kk: Number(e.target.value) }))} />
            </label>
            <button className="nappi nappi--kevyt" disabled={tyossa}
                    onClick={() => void aja(() => api.muokkaaRataa(r.id, { aktiivinen: !r.aktiivinen }))}>
              {r.aktiivinen ? t("osa.piilota") : t("osa.nayta")}
            </button>
            <button className="nappi nappi--kevyt" disabled={tyossa}
                    onClick={() => setAvoin(avoin === r.id ? null : r.id)}>
              {avoin === r.id ? t("hl.sulje") : t("osa.vaiheet_lkm", { n: r.vaiheet.length })}
            </button>
            <button className="nappi nappi--kevyt" disabled={tyossa}
                    onClick={() => void aja(() => api.poistaRata(r.id), t("osa.rata_poistettu"))}>
              {t("liite.poista")}
            </button>
          </div>
          {/* Sana "tuoreus" ilman määritelmää ei kerro kentällä mitään.
              Sama sana ja sama sääntö kuin matriisin vihjeessä (lepäävä). */}
          <p className="hint" style={{ margin: "4px 0 0" }}>
            {t("osa.tuoreus_vihje_alku", { kk: r.tuoreus_kk })} <b>{t("osa.lepaavaksi")}</b> {t("osa.tuoreus_vihje_loppu")}
          </p>

          {avoin === r.id && (
            <>
              <h3 style={{ marginTop: 16 }}>{t("osa.vaiheet")}</h3>
              <div className="taulukko-kaari">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th className="th--vasen">{t("iv.nimi")}</th>
                      <th style={{ width: 90 }}>{t("osa.jarjestys")}</th>
                      <th style={{ width: 200 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {r.vaiheet.map((v) => (
                      <tr key={v.id} className={v.aktiivinen ? undefined : "dim"}>
                        <td>
                          <input type="text" defaultValue={v.nimi} disabled={tyossa}
                                 style={{ width: "100%" }}
                                 onBlur={(e) => e.target.value !== v.nimi
                                   && void aja(() => api.muokkaaVaihetta(v.id, { nimi: e.target.value }))} />
                        </td>
                        <td>
                          <input type="number" defaultValue={v.jarjestys} disabled={tyossa}
                                 style={{ width: 70 }}
                                 onBlur={(e) => Number(e.target.value) !== v.jarjestys
                                   && void aja(() => api.muokkaaVaihetta(v.id, { jarjestys: Number(e.target.value) }))} />
                        </td>
                        <td>
                          <button className="nappi nappi--kevyt" disabled={tyossa}
                                  onClick={() => void aja(() => api.muokkaaVaihetta(v.id, { aktiivinen: !v.aktiivinen }))}>
                            {v.aktiivinen ? t("osa.piilota") : t("osa.nayta")}
                          </button>{" "}
                          <button className="nappi nappi--kevyt" disabled={tyossa}
                                  onClick={() => void aja(() => api.poistaVaihe(v.id), t("osa.vaihe_poistettu"))}>
                            {t("liite.poista")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <UusiVaihe rataId={r.id} tyossa={tyossa} aja={aja} />

              <h3 style={{ marginTop: 18 }}>{t("osa.tasot")}</h3>
              <div className="taulukko-kaari">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th className="th--vasen">{t("osa.taso")}</th>
                      <th className="th--vasen">{t("osa.saanto")}</th>
                      <th style={{ width: 80 }}>{t("osa.raja")}</th>
                      <th style={{ width: 100 }}>{t("osa.yksikoita")}</th>
                      <th style={{ width: 100 }}>{t("osa.vastuulla")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.tasot.map((tk) => (
                      <tr key={tk.id}>
                        <td>{tk.nimi}</td>
                        <td>
                          <select defaultValue={tk.saanto} disabled={tyossa}
                                  onChange={(e) => void aja(() => api.muokkaaTasoa(tk.id, { saanto: e.target.value }))}>
                            <option value="nimitys">{t("osa.saanto_nimitys")}</option>
                            <option value="kattavuus">{t("osa.saanto_kattavuus")}</option>
                            <option value="ketju">{t("osa.saanto_ketju")}</option>
                          </select>
                        </td>
                        <td>
                          <input type="number" min={0} defaultValue={tk.raja} disabled={tyossa}
                                 style={{ width: 60 }}
                                 onBlur={(e) => Number(e.target.value) !== tk.raja
                                   && void aja(() => api.muokkaaTasoa(tk.id, { raja: Number(e.target.value) }))} />
                        </td>
                        <td>
                          <input type="number" min={0} defaultValue={tk.eri_yksikkoa ?? 0} disabled={tyossa}
                                 style={{ width: 60 }}
                                 onBlur={(e) => void aja(() => api.muokkaaTasoa(tk.id, { eri_yksikkoa: Number(e.target.value) }))} />
                        </td>
                        <td>
                          <input type="number" min={0} defaultValue={tk.vastuuraja ?? 0} disabled={tyossa}
                                 style={{ width: 60 }}
                                 onBlur={(e) => void aja(() => api.muokkaaTasoa(tk.id, { vastuuraja: Number(e.target.value) }))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="ikkuna__vihje">
                <b>{t("osa.saanto_kattavuus")}</b> {t("osa.saanto_vihje_kattavuus")} ·{" "}
                <b>{t("osa.saanto_ketju")}</b> {t("osa.saanto_vihje_ketju")} ·{" "}
                <b>{t("osa.saanto_nimitys")}</b> {t("osa.saanto_vihje_nimitys")}
              </p>
            </>
          )}
        </div>
      ))}
    </section>
  );
}

function UusiVaihe({
  rataId, tyossa, aja,
}: {
  rataId: number; tyossa: boolean;
  aja: (teko: () => Promise<unknown>, viesti?: string) => Promise<void>;
}) {
  const [nimi, setNimi] = useState("");
  const tunnus = nimi.trim().toLowerCase()
    .replace(/[äå]/g, "a").replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);

  return (
    <div className="lomake__rivi" style={{ marginTop: 8, alignItems: "flex-end" }}>
      <label style={{ flex: "1 1 240px" }}>
        {t("osa.uusi_vaihe")}
        <input type="text" value={nimi} placeholder={t("osa.vaihe_esim")}
               onChange={(e) => setNimi(e.target.value)} />
      </label>
      <button className="nappi nappi--kevyt" disabled={tyossa || !tunnus}
              onClick={() => void aja(async () => {
                await api.luoVaihe({ rata_id: rataId, tunnus, nimi: nimi.trim(), jarjestys: 500 });
                setNimi("");
              }, t("osa.vaihe_lisatty"))}>
        {t("rot.lisaa")}
      </button>
    </div>
  );
}

/* ── Sertifikaatti ─────────────────────────────────────────
   Kolme ehtoa näkyvissä yhtä aikaa, myös silloin kun ne eivät
   täyty. Puuttuva ehto on tässä tärkeämpi tieto kuin täyttyvä:
   se kertoo mitä pitää tehdä, eikä sitä saa piilottaa nappia
   piilottamalla. Palvelin torjuu joka tapauksessa. */

function Sertifikaatit({
  henkiloId, saaArvioida, omaId,
}: {
  henkiloId: number; saaArvioida: boolean; omaId: number | null;
}) {
  const [ehdot, setEhdot] = useState<SertiEhdot[]>([]);
  const [sertit, setSertit] = useState<Sertifikaatti[]>([]);
  const [arvioinnit, setArvioinnit] = useState<Arviointi[]>([]);
  const [tyossa, setTyossa] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [tuloste, setTuloste] = useState<number | null>(null);

  const lataa = useCallback(async () => {
    try {
      const [e, s, a] = await Promise.all([
        api.osaamisEhdot(henkiloId),
        api.osaamissertifikaatit(henkiloId),
        api.osaamisarvioinnit(henkiloId),
      ]);
      setEhdot(e); setSertit(s); setArvioinnit(a);
    } catch { /* kortti näyttää oman virheensä */ }
  }, [henkiloId]);

  useEffect(() => { void lataa(); }, [lataa]);

  async function aja(teko: () => Promise<unknown>, viesti?: string) {
    setTyossa(true); setIlmoitus(null);
    try {
      await teko();
      if (viesti) setIlmoitus(viesti);
      await lataa();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("aca.toiminto_virhe"));
    } finally { setTyossa(false); }
  }

  const omaKortti = henkiloId === omaId;

  if (tuloste !== null) {
    return <SertifikaattiTuloste id={tuloste} sulje={() => setTuloste(null)} />;
  }

  return (
    <>
      <h3 style={{ marginTop: 22 }}>{t("osa.sertifikaatin_ehdot")}</h3>
      {ehdot.length === 0 ? (
        <p className="dim">{t("osa.ei_ratoja_serti")}</p>
      ) : (
        <div className="taulukko-kaari">
          <table className="tbl">
            <thead>
              <tr>
                <th className="th--vasen">{t("osa.rata")}</th>
                <th className="th--vasen">{t("osa.taso")}</th>
                <th>{t("osa.teoria")}</th>
                <th>{t("osa.mitattu_tyo")}</th>
                <th>{t("osa.arviointi")}</th>
                <th className="tuloste-tyokalut" />
              </tr>
            </thead>
            <tbody>
              {ehdot.map((e) => (
                <tr key={e.rata_id}>
                  <td>{e.rata}</td>
                  <td><b>{e.taso ?? "—"}</b>
                      {e.lepaava && <span className="dim"> · {t("osa.lepaava")}</span>}</td>
                  {/* 138 (2b): tyhjä rata ei enää näytä ✓:ltä. Syy sanotaan rivissä,
                      koska «puuttuu» yksin lähettää oppijan etsimään kurssia jota ei ole. */}
                  <td>{e.teoria ? "✓" : (
                        <>
                          <span style={{ color: "var(--puna)" }}>{t("osa.puuttuu")}</span>
                          {e.teoria_kursseja === 0
                            && <span className="dim"> · {t("osa.ei_julkaistua_kurssia")}</span>}
                        </>
                      )}</td>
                  <td>{e.tyo ? "✓" : <span style={{ color: "var(--puna)" }}>{t("osa.puuttuu")}</span>}</td>
                  <td>
                    {e.arviointi
                      ? <>✓ <span className="dim">{naytaPvm(e.arviointi_pvm)}</span></>
                      : <span style={{ color: "var(--puna)" }}>{t("osa.puuttuu")}</span>}
                  </td>
                  <td className="tuloste-tyokalut">
                    {saaArvioida && (
                      <button className="nappi nappi--paa" disabled={tyossa || !e.valmis}
                              onClick={() => void aja(async () => {
                                const s = await api.myonnaSertifikaatti(henkiloId, e.rata_id);
                                setIlmoitus(t("osa.serti_myonnetty", { nro: s.numero }));
                              })}>
                        {t("osa.myonna")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {saaArvioida && !omaKortti && ehdot.length > 0 && (
        <UusiArviointi henkiloId={henkiloId} radat={ehdot} tyossa={tyossa} aja={aja} />
      )}
      {saaArvioida && omaKortti && (
        <p className="ikkuna__vihje">
          {t("osa.oma_arviointi_vihje")}
        </p>
      )}

      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}

      {arvioinnit.length > 0 && (
        <>
          <h3 style={{ marginTop: 22 }}>{t("osa.arvioinnit")}</h3>
          <ul className="lista">
            {arvioinnit.map((a) => (
              <li key={a.id}>
                <b>{a.hyvaksytty ? t("osa.hyvaksytty") : t("osa.hylatty")}</b> · {a.rata} · {naytaPvm(a.paiva)}
                {a.arvioija && <span className="dim"> · {a.arvioija}</span>}
                {a.kohde && <span className="dim"> · {a.kohde}</span>}
                {a.huomiot && <span className="dim"> — {a.huomiot}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      <h3 style={{ marginTop: 22 }}>{t("osa.sertifikaatit")}</h3>
      {sertit.length === 0 ? (
        <p className="dim">{t("osa.ei_sertifikaatteja")}</p>
      ) : (
        <div className="taulukko-kaari">
          <table className="tbl">
            <thead>
              <tr>
                <th className="th--vasen">{t("osa.numero")}</th>
                <th className="th--vasen">{t("osa.rata_ja_taso")}</th>
                <th className="th--vasen">{t("osa.myonnetty")}</th>
                <th className="th--vasen">{t("osa.voimassa")}</th>
                <th className="th--vasen">{t("osa.myontaja")}</th>
                <th className="tuloste-tyokalut" />
              </tr>
            </thead>
            <tbody>
              {sertit.map((s) => (
                <tr key={s.id} className={s.voimassa ? undefined : "dim"}>
                  <td className="mono"><b>{s.numero}</b></td>
                  <td>{s.rata_nimi} · {s.taso_nimi}</td>
                  <td className="mono">{naytaPvm(s.myonnetty)}</td>
                  <td className="mono">
                    {s.peruttu ? t("osa.peruttu_pieni") : (naytaPvm(s.voimassa_asti) ?? t("osa.toistaiseksi"))}
                  </td>
                  <td>{s.myontaja ?? "—"}</td>
                  <td className="tuloste-tyokalut">
                    <button className="nappi nappi--kevyt" disabled={tyossa}
                            onClick={() => setTuloste(s.id)}>
                      {t("osa.tuloste")}
                    </button>{" "}
                    {saaArvioida && !s.peruttu && (
                      <button className="nappi nappi--kevyt" disabled={tyossa}
                              onClick={() => {
                                const syy = window.prompt(t("osa.perumisen_syy"));
                                if (syy === null) return;
                                void aja(() => api.peruSertifikaatti(s.id, syy), t("osa.peruttu_ilmoitus"));
                              }}>
                        {t("osa.peru")}
                      </button>
                    )}
                    {s.peruttu && s.perumisen_syy && (
                      <span className="dim">{s.perumisen_syy}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="ikkuna__vihje">
        {t("osa.serti_vihje")}
      </p>
    </>
  );
}

function UusiArviointi({
  henkiloId, radat, tyossa, aja,
}: {
  henkiloId: number; radat: SertiEhdot[]; tyossa: boolean;
  aja: (teko: () => Promise<unknown>, viesti?: string) => Promise<void>;
}) {
  const [rata, setRata] = useState<number | "">("");
  const [kohde, setKohde] = useState("");
  const [huomiot, setHuomiot] = useState("");

  return (
    <div className="tuloste-tyokalut" style={{ marginTop: 12 }}>
      <h3>{t("osa.kirjaa_arviointi")}</h3>
      <div className="lomake__rivi" style={{ alignItems: "flex-end" }}>
        <label>
          {t("osa.rata")}
          <select value={rata} disabled={tyossa}
                  onChange={(e) => setRata(e.target.value === "" ? "" : Number(e.target.value))}>
            <option value="">{t("osa.valitse")}</option>
            {radat.map((r) => <option key={r.rata_id} value={r.rata_id}>{r.rata}</option>)}
          </select>
        </label>
        <label>
          {t("kuittaus.kohde")}
          <input type="text" value={kohde} placeholder={t("osa.kohde_esim")}
                 onChange={(e) => setKohde(e.target.value)} />
        </label>
        <label style={{ flex: "1 1 220px" }}>
          {t("osa.huomiot")}
          <input type="text" value={huomiot}
                 onChange={(e) => setHuomiot(e.target.value)} />
        </label>
      </div>
      <div className="allokointi">
        <button className="nappi nappi--paa" disabled={tyossa || rata === ""}
                onClick={() => void aja(async () => {
                  await api.kirjaaArviointi({
                    henkilo_id: henkiloId, rata_id: rata as number, hyvaksytty: true,
                    kohde: kohde.trim() || null, huomiot: huomiot.trim() || null,
                  });
                  setKohde(""); setHuomiot("");
                }, t("osa.arviointi_hyvaksytty"))}>
          {t("osa.hyvaksy")}
        </button>
        <button className="nappi nappi--kevyt" disabled={tyossa || rata === ""}
                onClick={() => void aja(async () => {
                  await api.kirjaaArviointi({
                    henkilo_id: henkiloId, rata_id: rata as number, hyvaksytty: false,
                    kohde: kohde.trim() || null, huomiot: huomiot.trim() || null,
                  });
                  setKohde(""); setHuomiot("");
                }, t("osa.arviointi_hylatty"))}>
          {t("osa.hylkaa")}
        </button>
      </div>
      <p className="ikkuna__vihje">
        {t("osa.arviointi_vihje")}
      </p>
    </div>
  );
}

/* ── Sertifikaatin tuloste ─────────────────────────────────
   Asiakkaalle ja auditoijalle näytettävä dokumentti.

   Kaikki luvut luetaan JÄÄDYTETYSTÄ todisteesta eikä lasketa
   uudelleen. Jos tuloste laskisi tason nyt, viiden vuoden takainen
   sertifikaatti näyttäisi eri luvut kuin myöntöhetkellä — ja juuri
   se on se ominaisuus jonka takia todiste jäädytettiin.

   Todiste on mukana tulosteessa, ei vain numero. Sertifikaatin arvo
   on siinä että sen takana olevat suoritukset ovat jäljitettäviä;
   pelkkä nimi ja leima on se mitä kuka tahansa voi tulostaa. */

function SertifikaattiTuloste({
  id, sulje,
}: { id: number; sulje: () => void }) {
  const [s, setS] = useState<SertifikaattiTiedot | null>(null);
  const [brandi, setBrandi] = useState<Brandi | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);

  useEffect(() => {
    api.osaamissertifikaatti(id)
      .then(setS)
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("osa.serti_virhe")));
    void haeBrandi().then(setBrandi);
  }, [id]);

  if (virhe) return <p className="huom huom--vika" role="alert">{virhe}</p>;
  if (!s) return <p className="dim">{t("ltar.ladataan")}</p>;

  const taso = s.ehdot?.taso;
  const kurssit = s.ehdot?.kurssit ?? [];
  const arvio = s.ehdot?.arviointi;
  const peruttu = !!s.peruttu;
  const vanhentunut = !peruttu && !!s.voimassa_asti && s.voimassa_asti < uusiPvm();

  return (
    <div className="tuloste-overlay">
      <div className="tuloste-tyokalut">
        <button className="nappi nappi--kevyt" onClick={sulje}>{t("hl.sulje")}</button>{" "}
        <button className="nappi nappi--paa" onClick={() => window.print()}>{t("osa.tulosta")}</button>
      </div>

      <article className="tuloste">
        <header className="tuloste__paa">
          <TulosteLeima brandi={brandi} />
          <div className="tuloste__otsikko">
            <h1>{tDok("osa.osaamissertifikaatti")}</h1>
            <div className="tuloste__meta">
              <div className="mono"><b>{s.numero}</b></div>
              <div>{tDok("osa.myonnetty_pvm", { pvm: naytaPvm(s.myonnetty) ?? "" })}</div>
            </div>
          </div>
        </header>

        {(peruttu || vanhentunut) && (
          <p className="huom huom--vika" role="alert">
            {peruttu
              ? `${tDok("osa.peruttu_iso", { pvm: naytaPvm(s.peruttu) ?? "" })}${s.perumisen_syy ? ` — ${s.perumisen_syy}` : ""}`
              : tDok("osa.ei_voimassa", { pvm: naytaPvm(s.voimassa_asti) ?? "" })}
          </p>
        )}

        <section style={{ margin: "26px 0" }}>
          <div className="tuloste__osapuoli-otsikko">{tDok("osa.myonnetty_henkilolle")}</div>
          <div style={{ fontSize: 26, fontWeight: 700, margin: "4px 0 2px" }}>{s.nimi}</div>
          {s.lyhenne && <div className="dim mono">{s.lyhenne}</div>}
        </section>

        <section style={{ margin: "26px 0" }}>
          <div className="tuloste__osapuoli-otsikko">{tDok("osa.osaamisala_ja_taso")}</div>
          <div style={{ fontSize: 20, fontWeight: 600, margin: "4px 0" }}>
            {s.rata_nimi} · {s.taso_nimi}
          </div>
          <div className="dim">
            {tDok("osa.voimassa_pvm", { pvm: naytaPvm(s.voimassa_asti) ?? tDok("osa.toistaiseksi") })}
            {s.myontaja && ` · ${tDok("osa.myontaja_nimi", { nimi: s.myontaja })}`}
          </div>
        </section>

        <section style={{ margin: "26px 0" }}>
          <div className="tuloste__osapuoli-otsikko">{tDok("osa.perusteet")}</div>
          <p className="tuloste__ehto">
            {tDok("osa.perusteet_teksti")}
          </p>

          <table className="tbl" style={{ marginTop: 12 }}>
            <tbody>
              <tr>
                <td><b>{tDok("osa.mitattu_tyo")}</b></td>
                <td className="mono">
                  {taso ? tDok("osa.vaihesuoritusta", { n: taso.mitatut }) : "—"}
                  {taso && taso.eri_yksikkoa > 0 && ` · ${tDok("osa.eri_yksikkoa", { n: taso.eri_yksikkoa })}`}
                  {taso && taso.ketjuja > 0 && ` · ${tDok("osa.kokonaista_ketjua", { n: taso.ketjuja })}`}
                  {taso && taso.vastuulla > 0 && ` · ${tDok("osa.vastuuhenkilona_lkm", { n: taso.vastuulla })}`}
                </td>
              </tr>
              {taso && taso.hyvitetyt > 0 && (
                <tr>
                  <td>{tDok("osa.hyvitetty_kokemus")}</td>
                  <td className="mono">{tDok("osa.vaihesuoritusta", { n: taso.hyvitetyt })}</td>
                </tr>
              )}
              <tr>
                <td><b>{tDok("osa.kaytannon_arviointi")}</b></td>
                <td>
                  {arvio
                    ? <>{tDok("osa.hyvaksytty_pvm", { pvm: naytaPvm(arvio.paiva) ?? "" })}
                        {arvio.arvioija && ` · ${arvio.arvioija}`}
                        {arvio.kohde && ` · ${arvio.kohde}`}</>
                    : "—"}
                </td>
              </tr>
            </tbody>
          </table>

          {kurssit.length > 0 && (
            <>
              <div className="tuloste__osapuoli-otsikko" style={{ marginTop: 18 }}>
                {tDok("osa.suoritetut_kurssit")}
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th className="th--vasen">{tDok("aca.kurssi")}</th>
                    <th>{tDok("osa.pisteet")}</th>
                    <th className="th--vasen">{tDok("aca.suoritettu")}</th>
                    <th className="th--vasen">{tDok("osa.voimassa")}</th>
                  </tr>
                </thead>
                <tbody>
                  {kurssit.map((k, i) => (
                    <tr key={i}>
                      <td>{k.nimi}</td>
                      <td className="mono">{k.pisteet ?? "—"}</td>
                      <td className="mono">{naytaPvm(k.paiva)}</td>
                      <td className="mono">{naytaPvm(k.voimassa_asti) ?? tDok("osa.toistaiseksi")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>

        <p className="tuloste__alaviite">
          {brandi?.nimi ?? "Sampo Industrial"} · {tDok("osa.alaviite", { nro: s.numero })}
        </p>
      </article>
    </div>
  );
}

function uusiPvm(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
